import { Form } from 'antd';
import styled, { css } from 'styled-components';

/** Matches the exact hover chain `Ant/Button` declares, so rules here outrank it. */
const frozenHover = ':not(:disabled):not(.ant-btn-disabled):hover';

/**
 * antd already paints error/warning borders on its own controls, but not on the
 * `add-transaction-btn` lookup buttons, and it has no concept of a "help" status
 * at all.
 */
const fieldStatusBorder = (color: string) => css`
  .ant-input-outlined,
  .ant-input-number-outlined,
  .ant-picker-outlined {
    &,
    &:hover,
    &:focus,
    &:focus-within {
      border-color: ${color};
    }
  }
  .add-transaction-btn {
    &,
    &${frozenHover} {
      border-color: ${color};
    }
  }
`;

export type FieldMessageStatus = 'error' | 'warning' | 'help';

const withoutFieldStatus =
  ':not(.ant-form-item-has-error):not(.ant-form-item-has-warning):not(.has-help-border)';

/** antd drives error/warning itself, but knows no "help" status, so that rides on a class. */
export const fieldStatusValidation = (status?: FieldMessageStatus) =>
  status === 'error' || status === 'warning' ? status : undefined;

export const fieldStatusClassName = (
  status?: FieldMessageStatus,
  ...extraClassNames: (string | false | undefined)[]
) =>
  [...extraClassNames, status === 'help' && 'has-help-border'].filter(Boolean).join(' ') ||
  undefined;

export const FormItem = styled(Form.Item)`
  .ant-form-item-label {
    & > label {
      height: 18px;

      &.ant-form-item-required {
        &::before {
          position: absolute;
          right: 2px;
          top: 0;
          font-family: 'Poppins', sans-serif;
          font-size: 12px;
          line-height: 18px;
        }
      }
    }
    &.fw-500 {
      font-weight: 500;
    }
  }
  &.entries-tab-label .ant-form-item-label > label {
    font-weight: 500;
    color: var(--clr-subtitle);
  }
  &.ant-form-item-has-error {
    ${fieldStatusBorder('var(--clr-error-border)')}
  }
  &.ant-form-item-has-warning {
    ${fieldStatusBorder('var(--clr-inline-warning)')}
  }
  &.has-help-border {
    ${fieldStatusBorder('var(--clr-inline-help)')}
  }
  .recommended-label {
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }
  .recommended-suffix {
    color: var(--clr-general-description);
  }
  .recommended-info.ant-btn-icon-only {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    .ant-btn-icon {
      font-size: 12px;
    }
  }

  &.disabled {
    cursor: not-allowed;
    .ant-form-item-label,
    .ant-form-item-control-input {
      opacity: 0.5;
    }
    &.mail-action-subject {
      .ant-form-item-label,
      .ant-form-item-control-input {
        opacity: 1;
      }
    }
    /* A field marked disabled here may hold a button that is not itself disabled
       (a read-only display), so the button's own hover styling still applies.
       Repeating its :not() chain outranks that and freezes the background. */
    .add-transaction-btn {
      cursor: not-allowed;
      &,
      &${frozenHover} {
        background-color: var(--clr-container-bg);
      }
      &:not(:has(.preselection-field)) {
        &,
        &${frozenHover} {
          background-color: var(--clr-field-disabled);
        }
      }
    }
  }

  &.disabled${withoutFieldStatus} {
    .add-transaction-btn {
      &,
      &${frozenHover} {
        border-color: var(--clr-border-grey-100);
      }
    }
  }
  /* antd reserves a control's worth of height under the field for the extra slot; the
     inline messages size themselves, so it only takes the room they need. */
  .ant-form-item-additional .ant-form-item-extra {
    min-height: 0;
  }
`;
